import { ReactNode } from 'react';

import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { MaybeUndef } from 'types/UtilityTypes';
import { formatCurrency } from 'utils/formatCurrency';

import { LTLQuote } from '../types';

export enum LTLRateCardCoverageMode {
  Insurance = 'insurance',
  LimitedLiability = 'limitedLiability',
}

export interface LTLRateCardCoverageConfig {
  sectionTitle: string;
  defaultOpen: boolean;
  mode: LTLRateCardCoverageMode;
}

export function formatLTLCoverageAmount(amount: MaybeUndef<number>): string {
  if (amount == null) {
    return '-';
  }

  return formatCurrency(amount, 'USD');
}

interface LTLRateCardCoverageDetailsProps {
  quote: LTLQuote;
  mode: LTLRateCardCoverageMode;
}

interface CoverageLabelValueRowProps {
  label: string;
  value: string;
  indent?: boolean;
}

function CoverageLabelValueRow({
  label,
  value,
  indent = false,
}: CoverageLabelValueRowProps) {
  return (
    <Flex justify='between' className={indent ? 'w-full pl-2' : 'w-full'}>
      <Typography variant='body-sm' className='text-neutral-500 shrink-0'>
        {label}
      </Typography>
      <Typography
        variant='body-sm'
        weight='medium'
        className='text-neutral-800 tabular-nums text-right'
      >
        {value}
      </Typography>
    </Flex>
  );
}

function CoverageSectionLabel({ children }: { children: ReactNode }) {
  return (
    <Typography variant='body-sm' className='text-neutral-500'>
      {children}
    </Typography>
  );
}

function CoverageDetailsContainer({ children }: { children: ReactNode }) {
  return (
    <Flex direction='col' gap='xs' align='stretch' className='mt-2 w-full'>
      {children}
    </Flex>
  );
}

export function LTLRateCardCoverageDetails({
  quote,
  mode,
}: LTLRateCardCoverageDetailsProps) {
  if (mode === LTLRateCardCoverageMode.LimitedLiability) {
    return (
      <CoverageDetailsContainer>
        <CoverageLabelValueRow
          label='Coverage Type: '
          value={quote.coverageType || 'Limited Liability'}
        />
        <CoverageSectionLabel>Coverage Amount: </CoverageSectionLabel>
        <CoverageLabelValueRow
          label='New: '
          value={formatLTLCoverageAmount(quote.coverageAmountNew)}
          indent
        />
        <CoverageLabelValueRow
          label='Used: '
          value={formatLTLCoverageAmount(quote.coverageAmountUsed)}
          indent
        />
      </CoverageDetailsContainer>
    );
  }

  return (
    <CoverageDetailsContainer>
      {quote.coverageType && (
        <CoverageLabelValueRow
          label='Coverage Type: '
          value={quote.coverageType}
        />
      )}
      {quote.coverageAmountNew != null && (
        <CoverageLabelValueRow
          label='Coverage Amount: '
          value={formatLTLCoverageAmount(quote.coverageAmountNew)}
        />
      )}
    </CoverageDetailsContainer>
  );
}

export function shouldShowLTLRateCardCoverageSection(
  quote: LTLQuote,
  coverageConfig?: LTLRateCardCoverageConfig
): boolean {
  if (coverageConfig?.mode === LTLRateCardCoverageMode.LimitedLiability) {
    return !quote.isError && !quote.notServiced;
  }

  return Boolean(quote.coverageType || quote.coverageAmountNew != null);
}
